// ╔══════════════════════════════════════════════════════════════════╗
// ║                    ADMIN CRYPTOCURRENCY PAGE                     ║
// ╚══════════════════════════════════════════════════════════════════╝

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Bitcoin,
  Search,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  X,
  Save,
  RefreshCw,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Activity,
  Eye,
  EyeOff,
  BarChart2
} from 'lucide-react'
import { CryptoAdminAPI } from '../../services/adminApi'
import { useAdmin } from '../AdminApp'

export default function CryptoPage() {
  const { hasPermission } = useAdmin()
  const [cryptocurrencies, setCryptocurrencies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState(null)

  // Filters
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // Modal
  const [showModal, setShowModal] = useState(false)
  const [editingCrypto, setEditingCrypto] = useState(null)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    setError(null)

    try {
      console.log('[CryptoPage] Fetching data...')

      const [cryptoRes, statsRes] = await Promise.all([
        CryptoAdminAPI.getAll().catch(err => {
          console.error('[CryptoPage] getAll error:', err)
          return { success: false, error: err }
        }),
        CryptoAdminAPI.getStats().catch(err => {
          console.error('[CryptoPage] getStats error:', err)
          return { success: false, error: err }
        })
      ])

      console.log('[CryptoPage] cryptoRes:', cryptoRes)
      console.log('[CryptoPage] statsRes:', statsRes)

      if (cryptoRes.success) {
        console.log('[CryptoPage] Cryptocurrencies loaded:', cryptoRes.data)
        setCryptocurrencies(cryptoRes.data?.cryptocurrencies || cryptoRes.data || [])
      } else {
        console.error('[CryptoPage] cryptoRes failed:', cryptoRes)
        setError(`Failed to load cryptocurrencies: ${cryptoRes.message || cryptoRes.error || 'Unknown error'}`)
      }

      if (statsRes.success) {
        console.log('[CryptoPage] Stats loaded:', statsRes.data)
        setStats(statsRes.data)
      } else {
        console.warn('[CryptoPage] Stats failed (non-critical):', statsRes)
      }
    } catch (err) {
      console.error('[CryptoPage] Fetch error:', err)
      setError(`Failed to load cryptocurrency data: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveCrypto = async (cryptoData) => {
    setProcessing(true)
    try {
      const res = await CryptoAdminAPI.save(cryptoData, cryptoData.id)
      if (res.success) {
        fetchData()
        setShowModal(false)
        setEditingCrypto(null)
      }
    } catch (err) {
      console.error('Failed to save cryptocurrency:', err)
    } finally {
      setProcessing(false)
    }
  }

  const handleDeleteCrypto = async (cryptoId) => {
    if (!confirm('Are you sure you want to delete this cryptocurrency?')) return

    try {
      const res = await CryptoAdminAPI.delete(cryptoId)
      if (res.success) {
        fetchData()
      }
    } catch (err) {
      console.error('Failed to delete cryptocurrency:', err)
    }
  }

  const handleToggleStatus = async (cryptoId, currentStatus) => {
    try {
      const res = await CryptoAdminAPI.toggleStatus(cryptoId, !currentStatus)
      if (res.success) {
        fetchData()
      }
    } catch (err) {
      console.error('Failed to toggle status')
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: amount < 1 ? 6 : 2
    }).format(amount || 0)
  }

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0)
  }

  const formatMarketCap = (cap) => {
    if (cap >= 1e12) return `$${(cap / 1e12).toFixed(2)}T`
    if (cap >= 1e9) return `$${(cap / 1e9).toFixed(2)}B`
    if (cap >= 1e6) return `$${(cap / 1e6).toFixed(2)}M`
    return formatCurrency(cap)
  }

  const filteredCrypto = cryptocurrencies.filter(crypto => {
    const matchesSearch = !search ||
      crypto.symbol.toLowerCase().includes(search.toLowerCase()) ||
      crypto.name.toLowerCase().includes(search.toLowerCase())
    const matchesStatus = !statusFilter ||
      (statusFilter === 'active' && crypto.is_active) ||
      (statusFilter === 'inactive' && !crypto.is_active)
    return matchesSearch && matchesStatus
  })

  const getChangeColor = (change) => {
    if (change > 0) return 'text-emerald-400'
    if (change < 0) return 'text-red-400'
    return 'text-slate-400'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Cryptocurrency</h1>
          <p className="text-slate-400 mt-1">Manage supported cryptocurrencies</p>
        </div>
        {hasPermission('crypto.edit') && (
          <button
            onClick={() => {
              setEditingCrypto(null)
              setShowModal(true)
            }}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Cryptocurrency
          </button>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Active Cryptos</p>
                <p className="text-2xl font-bold text-white">{formatNumber(stats.activeCryptos)}</p>
              </div>
              <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <Bitcoin className="w-5 h-5 text-orange-400" />
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total Holdings</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(stats.totalHoldings)}</p>
              </div>
              <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Active Traders</p>
                <p className="text-2xl font-bold text-white">{formatNumber(stats.activeTraders)}</p>
              </div>
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </div>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">24h Volume</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(stats.volume24h)}</p>
              </div>
              <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                <BarChart2 className="w-5 h-5 text-violet-400" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              placeholder="Search by symbol or name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={fetchData}
            className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Cryptocurrency Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : error ? (
          <div className="col-span-full flex items-center justify-center py-12">
            <div className="text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
              <p className="text-slate-400">{error}</p>
            </div>
          </div>
        ) : filteredCrypto.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-400">
            No cryptocurrencies found
          </div>
        ) : (
          filteredCrypto.map((crypto) => (
            <motion.div
              key={crypto.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-xl p-5"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold">{crypto.symbol?.slice(0, 2)}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{crypto.symbol}</h3>
                    <p className="text-slate-400 text-sm">{crypto.name}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  crypto.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {crypto.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Price</span>
                  <span className="text-white font-semibold">{formatCurrency(crypto.current_price)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">24h Change</span>
                  <div className="flex items-center gap-1">
                    {crypto.change_24h > 0 ? (
                      <TrendingUp className="w-4 h-4 text-emerald-400" />
                    ) : crypto.change_24h < 0 ? (
                      <TrendingDown className="w-4 h-4 text-red-400" />
                    ) : null}
                    <span className={`font-semibold ${getChangeColor(crypto.change_24h)}`}>
                      {crypto.change_24h > 0 ? '+' : ''}{crypto.change_24h?.toFixed(2)}%
                    </span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 text-sm">Market Cap</span>
                  <span className="text-white">{formatMarketCap(crypto.market_cap)}</span>
                </div>
              </div>

              {hasPermission('crypto.edit') && (
                <div className="flex gap-2 mt-4 pt-4 border-t border-slate-700">
                  <button
                    onClick={() => handleToggleStatus(crypto.id, crypto.is_active)}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                      crypto.is_active
                        ? 'bg-slate-700 hover:bg-slate-600 text-white'
                        : 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400'
                    }`}
                  >
                    {crypto.is_active ? (
                      <>
                        <EyeOff className="w-4 h-4" />
                        Disable
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" />
                        Enable
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setEditingCrypto(crypto)
                      setShowModal(true)
                    }}
                    className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteCrypto(crypto.id)}
                    className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>

      {/* Crypto Modal */}
      <AnimatePresence>
        {showModal && (
          <CryptoFormModal
            crypto={editingCrypto}
            onClose={() => {
              setShowModal(false)
              setEditingCrypto(null)
            }}
            onSave={handleSaveCrypto}
            processing={processing}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Crypto Form Modal Component
function CryptoFormModal({ crypto, onClose, onSave, processing }) {
  const [formData, setFormData] = useState({
    symbol: crypto?.symbol || '',
    name: crypto?.name || '',
    current_price: crypto?.current_price || 0,
    market_cap: crypto?.market_cap || '',
    circulating_supply: crypto?.circulating_supply || '',
    max_supply: crypto?.max_supply || '',
    description: crypto?.description || '',
    is_active: crypto?.is_active ?? true
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave({
      ...formData,
      id: crypto?.id
    })
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">
              {crypto ? 'Edit Cryptocurrency' : 'Add Cryptocurrency'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Symbol</label>
                <input
                  type="text"
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="BTC"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Current Price</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="number"
                    step="0.000001"
                    value={formData.current_price}
                    onChange={(e) => setFormData({ ...formData, current_price: parseFloat(e.target.value) })}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                placeholder="Bitcoin"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Market Cap</label>
                <input
                  type="number"
                  value={formData.market_cap}
                  onChange={(e) => setFormData({ ...formData, market_cap: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Circulating Supply</label>
                <input
                  type="number"
                  value={formData.circulating_supply}
                  onChange={(e) => setFormData({ ...formData, circulating_supply: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  placeholder="Optional"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Max Supply</label>
              <input
                type="number"
                value={formData.max_supply}
                onChange={(e) => setFormData({ ...formData, max_supply: e.target.value })}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                placeholder="Optional (e.g., 21000000 for Bitcoin)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500 resize-none"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-5 h-5 bg-slate-900/50 border border-slate-600 rounded"
              />
              <label htmlFor="is_active" className="text-slate-300">Active (available for trading)</label>
            </div>
          </div>

          <div className="p-6 border-t border-slate-700 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={processing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Save Cryptocurrency
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  )
}
